///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------
import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { InvoiceToPrint } from '../../services/PdfService'
import { getDateFromTimestamp, toFormatDate } from '../../services/DateService'
import Popup from '../Popup';
import { createNotification } from '../../services/NotificationService'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faEye, faDotCircle, faCreditCard, faBuildingColumns, faXmark, faDownload } from '@fortawesome/free-solid-svg-icons'
import { useReactToPrint } from 'react-to-print';
import { formatNr } from '../../services/FormatService';
import { userSideId } from '../../services/IdService';
import CardPaymentForm from './CardPaymentForm';

const Invoices = () => {
    const [loadingPrint, setLoadingPrint] = useState(false)
    const [printData, setPrintData] = useState({})
    const [sortedInvoices, setSortedInvoices] = useState([])
    const [invoiceDownload, setInvoiceDownload] = useState(false)
    const [openPopup, setOpenPopup] = useState(false)
    const [popupData, setPopupData] = useState()
    // 0 - Bank transfer; 1 - Online
    const [paymentType, setPaymentType] = useState('')

    const [cancelTarget, setCancelTarget] = useState(null)

    const {
        currentUser,
        invoiceData,
        fetchInvoiceData,
        fetchTaxData,
        taxData,
        loadingAction,
        cancelPendingOrder,
        cardPaymentsAvailable
    } = useAuth()

    const invoiceRef = useRef()

    useEffect(() => {
        if (invoiceData.length === 0) {
            fetchInvoiceData(currentUser.uid)
        }
        fetchTaxData(true, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // sort array
        const sortedArray = invoiceData.sort((a, b) =>
            (getDateFromTimestamp(a.timestamp) < getDateFromTimestamp(b.timestamp)) ? 1 : 
            (getDateFromTimestamp(a.timestamp) > getDateFromTimestamp(b.timestamp)) ? -1 : 0
        )
        setSortedInvoices(sortedArray)
    }, [invoiceData])

    const getTotalInvPrice = (val, quant, rate) => {
        const tvaVal = 21/100
        const getTotalWithTva = (val) => {
            return (+getTva(val) + val).toFixed(2)
        }
        const getValFromEur = (val) => {
            return (+val * (+rate)).toFixed(2)
        }
        const getTva = (val) => {
            return (tvaVal * val).toFixed(2)
        }
        return getTotalWithTva(getValFromEur(val) * (+quant))
    }

    const getColorByType = (type) => {
        switch(type) {
            case '0': // proforma
                return 'orange'
            case '1': // achitata
                return 'green'
            case '2': // anulata
                return 'red'
            default: // unknown
                return 'white'
        }
    }

    const getStatusByType = (type) => {
        switch(type) {
            case '0': // proforma
                return 'PROFORMĂ'
            case '1': // achitata
                return 'ACHITATĂ'
            case '2': // anulata
                return 'ANULATĂ'
            default: // unknown
                return ''
        }
    }

    const startPrint = useReactToPrint({
        content: () => invoiceRef.current,
    });

    //-----------------------------------------------------------------------------------------
    //* \brief
    //? The OBLIO document for an invoice, when there is one.
    //?
    //? Invoices issued through OBLIO are the fiscal ones, so both the preview and the
    //? download have to show that document rather than the copy we render ourselves.
    //? Proformas, and everything issued before the OBLIO integration, keep using the local
    //? renderer - which is why this returns '' rather than assuming.
    //-----------------------------------------------------------------------------------------
    const oblioLink = (invoice) => (invoice && invoice.oblio && invoice.oblio.link) || ''

    const openOblio = (invoice) => {
        createNotification('info', 'Se deschide factura fiscală')
        window.open(oblioLink(invoice), '_blank', 'noopener,noreferrer')
    }

    const printInvoice = async (data) => {
        if (loadingPrint) return
        createNotification('info', 'Se generează factura')
        setInvoiceDownload(false)
        setPrintData(data)
        setLoadingPrint(true)
        await new Promise(r => setTimeout(r, 1100));
        startPrint()
        await new Promise(r => setTimeout(r, 500));
        setLoadingPrint(false)
    }

    const downloadInvoice = async (data) => {
        if (loadingPrint) return
        createNotification('info', 'Se descarcă factura')
        setInvoiceDownload(true)
        setPrintData(data)
        setLoadingPrint(true)
        await new Promise(r => setTimeout(r, 500));
        setLoadingPrint(false)
        setInvoiceDownload(false)
    }

    const getInvoiceDate = (inv) => {
        const newDate = inv.status === '1' ? inv.paidDate : inv.timestamp
        return toFormatDate(getDateFromTimestamp(newDate))
    }

    // Payment
    const payWindow = (data) => {
        //? The card form is a full step of its own - showing the method chooser above it
        //? would only invite the customer to change their mind mid-form.
        if (paymentType === '1') {
            return <CardPaymentForm invoice={data} onClose={() => setPaymentType('')} />
        }

        return (
        <div className='md-form'>
            <div className='md-summary'>
                <div className='md-summary__item'>
                    <span>Factură</span>
                    <span>{data.seriesNumber}</span>
                </div>
                <div className='md-summary__item'>
                    <span>Abonament</span>
                    <span>{data.subscription.name} - {data.subscription.period} luni</span>
                </div>
                <div className='md-summary__item'>
                    <span>Total de plată (TVA inclus)</span>
                    <span style={{color:'var(--contrast-font-color)'}}><b>
                        {formatNr(getTotalInvPrice(data.subscription.priceEur, data.subscription.quantity, data.currencyRate))} Lei
                    </b></span>
                </div>
            </div>

            <div className='md-form__divider'></div>

            {cardPaymentsAvailable &&
            <>
                <div style={{textAlign:'center', fontWeight:'bold', paddingBottom:'0.5em'}}>
                    Alegeți modalitatea de plată
                </div>

                <div style={{display:'flex', gap:'0.75em', justifyContent:'center', flexWrap:'wrap'}}>
                    <button className='md-btn md-btn--primary' onClick={() => setPaymentType('1')}>
                        <FontAwesomeIcon icon={faCreditCard}/> Plată cu cardul
                    </button>
                    <button className='md-btn md-btn--ghost' onClick={() => setPaymentType('0')}>
                        <FontAwesomeIcon icon={faBuildingColumns}/> Transfer bancar
                    </button>
                </div>

                {
                    paymentType === '' &&
                    <div className='ap-popup-info' style={{textAlign:'left', marginTop:'1em'}}>
                        <div>
                            <b>Cu cardul</b> - activare <b>automată, imediată</b> după confirmarea plății.
                        </div>
                        <div style={{marginTop:'0.4em'}}>
                            <b>Prin transfer bancar</b> - activarea se face <b>după confirmarea încasării</b>
                            {' '}(de regulă 1-2 zile lucrătoare).
                        </div>
                    </div>
                }
            </>
            }

            {
                //? With card payments off there is nothing to choose between, so the bank
                //? details are shown straight away rather than behind a single button
                (paymentType === '0' || !cardPaymentsAvailable) &&
                <div className='ap-popup' style={{textAlign:'left', marginTop:'1em'}}>
                    <div className='ap-popup-info'>
                        <div><b>Date pentru plata prin transfer bancar</b></div>
                    </div>
                    <div><b>IBAN: </b><span style={{userSelect:'all'}}>{taxData.iban}</span> (<b>Atenție!</b> IBAN-ul a fost schimbat la data de 4 Aprilie 2026)</div>
                    <div><b>Bancă: </b>{taxData.bank}</div>
                    <div><b>Nume beneficiar: </b><span style={{userSelect:'all'}}>{taxData.name}</span></div>
                    <div><b>Detalii plată: </b>
                    <span style={{userSelect:'all'}}>
                        { userSideId(data.subscription.id) }
                    </span>
                    </div>
                    <div><b>CUI beneficiar: </b>{taxData.cif}</div>
                    <div><b>Total de plată: </b>
                        <span style={{userSelect:'all'}}>
                        {formatNr(getTotalInvPrice(data.subscription.priceEur, data.subscription.quantity, data.currencyRate))} Lei
                        </span>
                    </div>
                    <br />
                    <div className='ap-popup-hint'>
                        Abonamentul se activează după confirmarea încasării. Pentru o activare mai rapidă
                        ne puteți trimite dovada plății la <span style={{userSelect:'all'}}>programintrastat@gmail.com</span>.
                    </div>
                </div>
            }
        </div>
    )}

    const openPopupWindow = (data) => {
        const dataToAdd = {
            type: 3,
            title: data.title,
            content: data.content
        }
        setPopupData(dataToAdd)
        setOpenPopup(true)
    }

    const payNow = (data) => {
        setPaymentType('')
        const dataForPopup = {
            title: `Achită factura ${data.seriesNumber} - ${getInvoiceDate(data)}`,
            content: data
        }
        openPopupWindow(dataForPopup)
    }

    return (
        <>
        <div className='app-default-wrapper'>
            <div className='app-default-section'>
                <h2>Istoric plăți</h2>
            </div>
            {
                loadingAction ?
                <FontAwesomeIcon className='spinner' icon={faSpinner}/> :

                <>
                <div className='invoice-scroll'>
                    { invoiceData.length === 0 && 'Nu aveți nici o factură' }
                    {
                    sortedInvoices.length > 0 && sortedInvoices.map((invoice, index) => {
                        if (invoice.status === '2') return null
                        return(
                            <div className='invoice-tab' key={`${index}-${invoice.id}`}>
                                <FontAwesomeIcon style={{color:getColorByType(invoice.type)}}
                                className='left-top-icon' icon={faDotCircle}/>
                                <table className='invoice-table'>
                                    <thead>
                                        <tr>
                                            <th>Serie și nr</th>
                                            <th>Dată</th>
                                            <th>Stare</th>
                                            <th>Factură</th>
                                            <th>Acțiuni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className='table-id'>{invoice.status !== '2' && `${invoice.series}${invoice.number}`}</td>
                                            <td>{ invoice.status !== '2' && getInvoiceDate(invoice) }</td>
                                            <td ><div className='state-bubble' style={{backgroundColor:getColorByType(invoice.type)}}>{getStatusByType(invoice.status)}</div></td>
                                            <td>
                                                { invoice.status !== '2' &&
                                                    <FontAwesomeIcon
                                                        onClick={() => oblioLink(invoice) ? openOblio(invoice) : printInvoice(invoice)}
                                                        title={oblioLink(invoice) ? 'Vezi factura fiscală' : 'Vezi factura'}
                                                        className='button-secondary adm-btn no-margin' icon={faEye}/> }
                                            </td>
                                            {
                                            invoice.status === '0' ?
                                            <td>
                                                <div className='inv-actions'>
                                                    <button className='inv-action inv-action--pay'
                                                        onClick={() => payNow(invoice)}
                                                        title='Achită factura'>
                                                        <FontAwesomeIcon icon={faCreditCard} />
                                                        <span>Achită</span>
                                                    </button>
                                                    <button className='inv-action inv-action--cancel'
                                                        onClick={() => setCancelTarget(invoice)}
                                                        title='Anulează comanda'>
                                                        <FontAwesomeIcon icon={faXmark} />
                                                        <span>Anulează</span>
                                                    </button>
                                                </div>
                                            </td> :
                                            <td>
                                                <div className='inv-actions'>
                                                    <button className='inv-action inv-action--download'
                                                        onClick={() => oblioLink(invoice) ? openOblio(invoice) : downloadInvoice(invoice)}
                                                        title={oblioLink(invoice)
                                                            ? 'Descarcă factura fiscală'
                                                            : 'Descarcă factura în format PDF'}>
                                                        <FontAwesomeIcon icon={faDownload} />
                                                        <span>Descarcă</span>
                                                    </button>
                                                </div>
                                            </td>
                                            }
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )
                    })
                    }

                </div>
                <div className='history-row'>
                    <p>
                    <FontAwesomeIcon style={{color:'green'}} icon={faDotCircle}/> - Achitată 
                    </p>
                    <p>
                    <FontAwesomeIcon style={{color:'orange'}} icon={faDotCircle}/> - Neachitată 
                    </p>
                    {/* <p>
                    <FontAwesomeIcon style={{color:'red'}} icon={faDotCircle}/> - Anulată
                    </p> */}
                </div>
                </>
            }
        </div>
        {
            openPopup &&
            <Popup data={popupData} close={() => setOpenPopup(false)}>
                {payWindow(popupData.content)}
            </Popup>
        }
        {
            cancelTarget &&
            <Popup
                data={{ type: 3, title: `Anulați comanda ${cancelTarget.seriesNumber}?` }}
                close={() => setCancelTarget(null)}
            >
                <div className='md-form'>
                    <div className='ap-popup-info' style={{textAlign:'left'}}>
                        <div>Se anulează comanda pentru abonamentul <b>{cancelTarget.subscription.name}</b>.</div>
                        <div style={{marginTop:'0.4em'}}>
                            Abonamentul neactivat se dezactivează, iar <b>abonamentul anterior, dacă mai
                            este valabil, redevine activ</b>.
                        </div>
                        <div style={{marginTop:'0.4em'}}>
                            După anulare puteți alege din nou orice abonament.
                        </div>
                    </div>
                    <div className='ap-popup-hint' style={{marginTop:'0.75em'}}>
                        Anularea este posibilă doar pentru comenzile neachitate. Nu se reține nicio sumă.
                    </div>
                    <div className='md-footer' style={{marginTop:'1em'}}>
                        <button className='md-btn md-btn--ghost' onClick={() => setCancelTarget(null)}>
                            Renunță
                        </button>
                        <button className='md-btn md-btn--danger'
                            onClick={async () => {
                                const target = cancelTarget
                                setCancelTarget(null)
                                await cancelPendingOrder(target)
                            }}>
                            {loadingAction
                                ? <FontAwesomeIcon className='spinner' icon={faSpinner}/>
                                : 'Anulează comanda'}
                        </button>
                    </div>
                </div>
            </Popup>
        }
        {
            loadingPrint &&
            <section className='hidden'>
                <InvoiceToPrint ref={invoiceRef} data={printData} toDownload = {invoiceDownload} /> 
            </section> 
        }
        </>
    )
}

export default Invoices